<script setup lang="ts">
import { LAWYER_TYPE_LAWYER } from '@/shared/constants/lawyerTypeConstants';
import EmblaCarousel from '@/shared/ui/EmblaCarousel/index';
import LawyerCard from '@/widgets/LawyerCard/index';
import AboutSpecialist from '@/pages/lawyer/ui/sections/MainSection/AboutSpecialist.vue';
import SpecializationList from '@/pages/lawyer/ui/sections/MainSection/SpecializationList.vue';
import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import { buildCatalogUrl } from '@/shared/utils/buildCatalogUrl';
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import type { ILawyerCardInfo } from '@/entities/lawyer/model/types/lawyer';

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject(LAWYER_INFO);

const workers: ComputedRef<ILawyerCardInfo[]> = computed(() =>
  (lawyerInfo?.value?.company?.workers ?? []).flatMap((worker) =>
    worker.lawyer ? [worker.lawyer] : [],
  ),
);

const isCompany: ComputedRef<boolean> = computed(() =>
  Boolean(lawyerInfo?.value?.company_id),
);

const workerDetailLink = (worker: ILawyerCardInfo): string => {
  const citySlug = worker.addresses?.[0]?.city?.slug;
  if (!citySlug || !worker.slug) return ROOT_CATALOG_URL;
  return buildCatalogUrl({ segments: ['geo', citySlug, 'lawyers', worker.slug] });
};
const isHasWorkers: ComputedRef<boolean> = computed(() => workers.value.length > 0);

const sliderTitle: ComputedRef<string> = computed(() =>
  lawyerInfo?.value?.role === LAWYER_TYPE_LAWYER
    ? 'Юристы компании'
    : 'Адвокаты компании',
);
</script>

<template>
  <section id="main-profile" :class="['lawyer-main-section', { _mobile: isMobile }]">
    <div class="lawyer-main-section__item">
      <AboutSpecialist />
    </div>
    <div
      v-if="isCompany && isHasWorkers"
      :class="[
        'lawyer-main-section__item',
        '_slider',
        { _custom: workers.length < 3 && !isMobile },
      ]"
    >
      <h3 class="lawyer-main-section__slider-title">
        {{ sliderTitle }} <span>({{ workers.length }})</span>
      </h3>
      <div class="lawyer-main-section__slider-wrapper">
        <EmblaCarousel
          class="lawyer-main-section__slider"
          :options="{ slidesToScroll: 1, align: 'start', containScroll: 'trimSnaps' }"
        >
          <div
            v-for="worker in workers"
            :key="worker.id"
            class="lawyer-main-section__slide"
          >
            <NuxtLink
              class="lawyer-main-section__slide-link"
              :to="workerDetailLink(worker)"
            >
              <LawyerCard
                :data="worker"
                :is-show-caption="false"
                is-lawyer-company-card
                is-push-to-feedbacks
              />
            </NuxtLink>
          </div>
        </EmblaCarousel>
      </div>
    </div>
    <div class="lawyer-main-section__item">
      <SpecializationList />
    </div>
  </section>
</template>

<style lang="less">
.lawyer-main-section {
  .box-shadow();

  background: #fff;
  border-radius: @main-size;
  padding: @size-x8 @size-x8 67px;

  &__item:not(:last-child) {
    margin-bottom: 30px;

    &._slider {
      margin-bottom: 50px;
    }
  }

  &__slider-title {
    font-weight: 600;
    font-size: @size-x6;
    line-height: 25px;
    margin: 0 0 @size-x5;

    span {
      font-size: @size-x4;
      line-height: 22px;
      font-weight: 400;
      color: @color-dove-gray;
    }
  }

  &__slider .embla__container {
    gap: @size-x5;
  }

  &__slider-wrapper {
    .box-shadow();

    background: none;
    border-radius: @main-size;
  }

  &__item._slider._custom .embla__viewport {
    overflow: unset;
  }

  &__item._slider._custom &__slider-wrapper {
    box-shadow: none;
  }

  &__slide {
    flex: 0 0 auto;
    width: calc((100% - 2 * @size-x5) / 3);
    max-width: 100%;
  }

  &__slide-link {
    display: block;
    height: 100%;
  }

  &__slide .lawyer-card {
    padding: @size-x7;
    height: 100%;
  }

  &._mobile {
    padding: @size-x6 @size-x4 30px;
  }

  &._mobile &__slider-title {
    font-size: @size-x4;
    line-height: 22px;
    margin-bottom: @size-x4;
  }

  &._mobile &__slider .embla__container {
    gap: 0;
  }

  &._mobile &__slide {
    width: 200px;
    padding-left: @size-x6;

    &:first-child {
      padding-left: @size-x4;
    }
  }

  &._mobile &__slide .lawyer-card {
    padding: @size-x6;
  }

  &._mobile &__slider-wrapper {
    box-shadow: none;
  }

  &._mobile &__slider .embla__viewport {
    padding: 5px 50px 25px 0;
  }
}
</style>
